import { AfterViewInit, Directive, ElementRef, HostBinding, Input, Optional } from '@angular/core';
import { RoutingService } from 'src/app/services/routing.service';
import { AccordionPanelComponent } from '../components/accordion-panel/accordion-panel.component';

/**
 * Put on the link of a list row that opens another entity page, with the same key passed to
 * RoutingService.navigateToChild. When the user comes back, the enclosing accordion panel is opened
 * and that row is scrolled into view and focused.
 */
@Directive({
  selector: '[appReturnFocus]',
  standalone: true,
})
export class ReturnFocusDirective implements AfterViewInit {
  @Input('appReturnFocus') returnKey = '';
  // Visible focus ring (styles.scss) - the browser hides its own after focus is moved by code
  @HostBinding('class.return-focus-link') readonly returnFocusLink = true;

  constructor(
    private el: ElementRef<HTMLElement>,
    private routingService: RoutingService,
    @Optional() private accordionPanel: AccordionPanelComponent | null,
  ) {}

  ngAfterViewInit(): void {
    if (!this.returnKey || !this.routingService.consumeReturnKey(this.returnKey)) {
      return;
    }
    this.accordionPanel?.open();
    // Wait for the surrounding section to finish opening before scrolling
    setTimeout(() => {
      this.revealInOwnScroller(this.el.nativeElement);
      this.el.nativeElement.focus({ preventScroll: true });
    }, 300);
  }

  /**
   * Bring the row into view inside the list's own scroll container only.
   * scrollIntoView is deliberately not used: it also scrolls html/body, which would move the whole
   * page (body is a scroller of its own, see styles.scss), and html has scroll-behavior: smooth.
   */
  private revealInOwnScroller(el: HTMLElement): void {
    const scroller = this.closestScroller(el);
    if (!scroller) {
      return;
    }
    const elRect = el.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    if (elRect.top < scrollerRect.top) {
      scroller.scrollTop -= scrollerRect.top - elRect.top;
    } else if (elRect.bottom > scrollerRect.bottom) {
      scroller.scrollTop += elRect.bottom - scrollerRect.bottom;
    }
  }

  /** Nearest scrollable ancestor, never the document itself. */
  private closestScroller(el: HTMLElement): HTMLElement | null {
    for (let parent = el.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
      const overflowY = getComputedStyle(parent).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
    }
    return null;
  }
}
